import React, {useCallback, useMemo, useState} from "react";
import {Button} from "antd";
import {DownloadOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {TICKET_LIST_STATES} from "constants/ticket";
import {useTicketExport} from "./useTicketExport";
import {TicketFilters} from "./TicketFilters";
import {TicketsTable} from "./TicketsTable";
import {TicketDetailModal} from "../detail/TicketDetailModal";

const defaultFilters = {
    number: "",
    search: "",
    role: "",
    assignedUserId: "",
    priority: "",
    status: "",
    companyId: "",
    subCompanyId: "",
    skip: 0,
    limit: 20
};

const withoutEmptyValues = filters =>
    Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ""));

export const TicketList = ({
    scope,
    category,
    state,
    ownerId,
    readOnlyDetail = false,
    showExport = true,
    compact = false,
    ownerView = false,
    roleOptions,
    roleLabels,
    refreshKey = 0,
    onChanged
}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const [filters, setFilters] = useState(defaultFilters);
    const [detailId, setDetailId] = useState(null);
    const [localRefreshKey, setLocalRefreshKey] = useState(0);
    const [exporting, exportTickets] = useTicketExport();

    const handleFiltersChange = useCallback(
        changes => setFilters(previous => ({...previous, ...changes, skip: 0})),
        []
    );

    const queryParams = useMemo(
        () => ({scope, state, category, ownerId, ...withoutEmptyValues(filters)}),
        [scope, state, category, ownerId, filters]
    );

    const [loading, tickets, , , refetch] = useFetch({
        fetchFn: stores.ticketStore.getTickets.bind(null, queryParams),
        defaultDataValue: {},
        dependencies: [JSON.stringify(queryParams), refreshKey + localRefreshKey]
    });

    // Üst bileşen yenilemeyi yönetiyorsa (sekme sayıları) yalnızca onu bilgilendirir; liste
    // refreshKey ile tek seferde yenilenir
    const handleChanged = () => {
        if (onChanged) {
            onChanged();
        } else {
            setLocalRefreshKey(previous => previous + 1);
        }
    };

    const handleExport = () => {
        const {skip, limit, ...exportParams} = queryParams;
        return exportTickets(exportParams);
    };

    return (
        <>
            {!compact && (
                <TicketFilters
                    category={category}
                    filters={filters}
                    roleOptions={roleOptions}
                    onChange={handleFiltersChange}
                    extra={
                        showExport && (
                            <Button
                                icon={<DownloadOutlined />}
                                loading={exporting}
                                onClick={handleExport}
                            >
                                {t("list.excel.button", {ns: "tickets"})}
                            </Button>
                        )
                    }
                />
            )}
            <TicketsTable
                category={category}
                rows={tickets?.data?.rows || []}
                total={tickets?.data?.dataCount || 0}
                loading={loading}
                pagination={filters}
                paginated={state !== TICKET_LIST_STATES.closed}
                compact={compact}
                ownerView={ownerView}
                roleLabels={roleLabels}
                onView={record => setDetailId(record._id)}
                onPageChange={(page, pageSize) =>
                    setFilters(previous => ({
                        ...previous,
                        skip: (page - 1) * pageSize,
                        limit: pageSize
                    }))
                }
            />
            <TicketDetailModal
                ticketId={detailId}
                readOnly={readOnlyDetail}
                roleOptions={roleOptions}
                roleLabels={roleLabels}
                onClose={() => setDetailId(null)}
                onChanged={handleChanged}
            />
        </>
    );
};
